

// ------------------------------------------

import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomEditorHosted from "../../components/common/CustomEditorHosted";
import AddQuestion from "./AddQuestion";
import { addQuiz, getDetails, editQuiz } from "../../services/quiz";
import { getAllDropdowns } from "../../services/college";
import { generateConfirm } from "../../helpers";

// Simplified schema to match only the payload fields
const quizSchema = yup.object().shape({
  category: yup.string().required("Category is required."),
  subCategory: yup.string().required("Sub Category is required."),
  quizName: yup
    .string()
    .required("Quiz Name is required.")
    .min(2, "Minimum Quiz Name is 2 characters")
    .max(200, "Maximum Quiz Name is 200 characters"),
  difficulty: yup.string().required("Difficulty level is required."),
  AboutQuiz: yup.string(), // Optional field
  avgTime: yup.string().required("Total time is required."),
});

const AddQuiz = () => {
  const navigate = useNavigate();
  const { quizId: urlQuizId } = useParams(); // Get quizId from URL

  // fetch  dropdowns

  const [dropdowns, setDropdowns] = useState({
    category: [],
    subCategory: [],
    difficulty: [
      { id: 0, name: "Easy" },
      { id: 1, name: "Intermediate" },
      { id: 2, name: "Difficult" },
    ],
    totalTime: [
      { id: 1, name: "15 mins" },
      { id: 2, name: "30 mins" },
      { id: 3, name: "45 mins" },
    ],
  });

  // Fetch dropdowns
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        let response = await getAllDropdowns("quiz_categories,quiz_sub_categories");
  
        if (response?.data?.statusCode === 200) {
          setDropdowns((prev) => ({
            ...prev,
            category: response?.data?.result?.quiz_categories || [],
            subCategory: response?.data?.result?.quiz_sub_categories || [],
          }));
        }
      } catch (error) {
        console.error("Error fetching dropdowns:", error);
      }
    };
  
    fetchDropdowns();
  }, []);

  const [activeTab, setActiveTab] = useState("addQuiz");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use localStorage to maintain quiz_id across tab navigation
  const storedQuizId = localStorage.getItem("current_quiz_id");
  const [quizId, setQuizId] = useState(urlQuizId || storedQuizId || null);
  const [isEditMode, setIsEditMode] = useState(!!urlQuizId || !!storedQuizId);

  const [aboutQuiz , setAboutQuiz]= useState("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(quizSchema),
    defaultValues: {
      category: "",
      subCategory: "",
      quizName: "",
      difficulty: "",
      avgTime: "",
      AboutQuiz: "",
    },
  });

  // Update localStorage whenever quizId changes
  useEffect(() => {
    if (quizId) {
      localStorage.setItem("current_quiz_id", quizId);
      setIsEditMode(true);
    }
  }, [quizId]);
  // Edit start
  // Fetch quiz details when quizId exists (edit mode)
  useEffect(() => {
    if (quizId && dropdowns.category.length > 0 && dropdowns.subCategory.length > 0) {
      (async () => {
        try {
          const response = await getDetails(quizId);
          if (response?.data?.statusCode === 200) {
            const quizData = response?.data?.result;
  
            setValue("category", String(quizData.category)); // Ensure type match
            setValue("subCategory", String(quizData.sub_category));
            setValue("quizName", quizData.quiz_name);
            setValue("difficulty", String(quizData.difficulty_level));
            setValue("avgTime", quizData.total_time);
            setValue("AboutQuiz", quizData.about_quiz);
            setAboutQuiz(quizData.about_quiz);
          } else {
            toast.error("Failed to fetch quiz details.");
          }
        } catch (error) {
          console.error("Error fetching quiz details:", error);
          toast.error("Something went wrong.");
        }
      })();
    }
  }, [quizId, dropdowns]); 

  const onSubmit = async (formData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      category: formData.category,
      sub_category: formData.subCategory,
      quiz_name: formData.quizName,
      difficulty_level: formData.difficulty,
      about_quiz: formData.AboutQuiz,
      total_time: formData.avgTime,
      ...(quizId && { quiz_id: quizId }), // Include quiz_id for edit mode
    };

    try {
      const response = quizId
        ? await editQuiz(payload)
        : await addQuiz(payload);

      if (response.data.statusCode === 200) {
        toast.success(
          response.data.statusMessage ||
            (quizId ? "Quiz updated successfully!" : "Quiz added successfully!")
        );

        if (!quizId) {
          // If adding a quiz, get the quizId from response and store it
          const newQuizId = response.data.result?.quiz_id;
          if (newQuizId) {
            setQuizId(newQuizId);
            localStorage.setItem("current_quiz_id", newQuizId);
            navigate(`/add-questions/${newQuizId}`);
          }
        } else {
          // If editing, navigate to questions with the same quizId
          navigate(`/add-questions/${quizId}`);
        }
      } else {
        toast.error(response.data.statusMessage || "Operation failed.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error?.response?.data?.statusMessage || "Something went wrong."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  console.log(quizId);

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="add-college">
          <div className="subheader mb-3">
            <div className="d-flex">
              <h2
                className="heading-36 me-4 active-head"
                style={{ cursor: "pointer", fontSize: "22px" }}
              >
                {isEditMode ? "Edit Quiz" : "Add Quiz"}
              </h2>
              <h2
                className="heading-36"
                style={{
                  cursor: quizId ? "pointer" : "not-allowed",
                  opacity: quizId ? 1 : 0.5,
                }}
                onClick={() => quizId && navigate(`/add-questions/${quizId}`)}
              >
                {quizId ? "Edit Questions" : "Add Questions"}
              </h2>
            </div>

            <div className="subheader-right-btm">
              <button
                type="button"
                className="btn-purple"
                onClick={() =>
                  generateConfirm(
                    "Are you sure you want to Discard Changes ?",
                    "Press Yes if you want to discard",
                    "quiz-discard",
                    () => {
                      navigate("/quiz-list");
                    }
                  )
                }
              >
                Discard
              </button>
              <button
                type="submit"
                className="btn-purple discard"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : quizId ? "Update" : "Save"}
              </button>
            </div>
          </div>

          {activeTab === "addQuiz" ? (
            <>
              <div className="addoverview-data row mb-3">
                <div className="col-md-4">
                  <h3 className="headingwithicon">
                    <FontAwesomeIcon className="play-icon" icon={faPlay} />
                    Choose Category
                  </h3>
                  <Form.Select
                    className="form-control"
                    {...register("category")}
                    aria-label="Select Category"
                  >
                    <option value="">Select Category</option>
                    {dropdowns?.category?.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>

                  {errors.category && (
                    <div className="input-field-error">
                      {errors.category.message}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <h3 className="headingwithicon">
                    <FontAwesomeIcon className="play-icon" icon={faPlay} />
                    Choose Sub Category
                  </h3>
                  <Form.Select
                    className="form-control"
                    {...register("subCategory")}
                    aria-label="Select Sub Category"
                  >
                    <option value="">Select Sub Category</option>
                    {dropdowns?.subCategory?.map((subCat) => (
                      <option key={subCat.id} value={subCat.id}>
                        {subCat.name}
                      </option>
                    ))}
                  </Form.Select>

                  {errors.subCategory && (
                    <div className="input-field-error">
                      {errors.subCategory.message}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <h3 className="headingwithicon">
                    <FontAwesomeIcon className="play-icon" icon={faPlay} />
                    Quiz Name
                  </h3>
                  <input className="form-control quiz-input" {...register("quizName")} 
                  placeholder="Enter Quiz Name"/>
                  {errors.quizName && (
                    <div className="input-field-error">
                      {errors.quizName.message}
                    </div>
                  )}
                </div>

                <div className="col-md-4">
                  <h3 className="headingwithicon">
                    <FontAwesomeIcon className="play-icon" icon={faPlay} />
                    Difficulty Level
                  </h3>
                  <Form.Select
                    className="form-control"
                    {...register("difficulty")}
                    aria-label="Select Difficulty"
                  >
                    <option value="">Select Difficulty</option>
                    <option value="0">Easy</option>
                    <option value="1">Intermediate</option>
                    <option value="2">Difficult</option>
                  </Form.Select>
                  {errors.difficulty && (
                    <div className="input-field-error">
                      {errors.difficulty.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="addoverview-data row mb-3">
                <div className="col-md-12">
                  <h6>About Quiz</h6>
                  <CustomEditorHosted
                    initialValue={aboutQuiz || ""}
                    onChange={(e) =>
                      setValue("AboutQuiz", e.target.getContent()?.replaceAll(`"`, "%22")
                      ?.replaceAll(`'`, "%27"))
                    }
                    
                          
                  />
                </div>
                <div className="col-md-4">
                  <h3 className="headingwithicon">
                    <FontAwesomeIcon className="play-icon" icon={faPlay} />
                    Total Time (in minutes)
                  </h3>
                  <Form.Control
                    type="number"
                    className="form-control quiz-input"
                    {...register("avgTime")}
                    placeholder="Enter Total Time in minutes"
                    aria-label="Enter Total Time"
                  />
                  {errors.avgTime && (
                    <div className="input-field-error">
                      {errors.avgTime.message}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <AddQuestion />
          )}
        </div>
      </form>
    </div>
  );
};

export default AddQuiz;
